type ApprovalApiEnvelope = Record<string, unknown>;

export type ApprovalJobBlock = {
  id: string;
  source_code: string;
  area: string;
  label: string;
  status: 'planned' | 'completed' | 'skipped' | 'cancelled';
  completed_at: string | null;
  completion_notes: string | null;
  points: number;
};

export type ApprovalJob = {
  id: string;
  site_id: string;
  site_name: string;
  scheduled_for: string;
  assigned_user_id: string;
  assigned_user_name: string;
  status: 'completed' | 'approved';
  total_points: number;
  completed_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  completion_notes: string | null;
  blocks: ApprovalJobBlock[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown, keys: string[]): string | null {
  if (!isRecord(value)) return null;
  for (const key of keys) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }
  return null;
}

function readNumber(value: unknown, keys: string[]): number | null {
  if (!isRecord(value)) return null;
  for (const key of keys) {
    const raw = value[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
      const parsed = Number.parseFloat(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function readArray(value: unknown, keys: string[]): unknown[] {
  if (!isRecord(value)) return [];
  for (const key of keys) {
    const candidate = value[key];
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractError(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (isRecord(payload)) {
    const message = readString(payload, ['message', 'error', 'details']);
    if (message) return message;
  }
  return fallback;
}

async function requestJson<T>(path: string, sessionToken: string | null, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');
  if (sessionToken) headers.set('Authorization', `Bearer ${sessionToken}`);

  const response = await fetch(path, { ...init, headers });
  const rawText = await response.text();
  const parsed = rawText ? safeParse(rawText) : null;

  if (!response.ok) {
    throw new Error(extractError(parsed, `${response.status} ${response.statusText}`));
  }

  return parsed as T;
}

function parseBlock(value: unknown): ApprovalJobBlock | null {
  if (!isRecord(value)) return null;

  return {
    id: readString(value, ['id']) ?? '',
    source_code: readString(value, ['source_code', 'sourceCode']) ?? '—',
    area: readString(value, ['area']) ?? '—',
    label: readString(value, ['label']) ?? '—',
    status: (readString(value, ['status']) as ApprovalJobBlock['status'] | null) ?? 'planned',
    completed_at: readString(value, ['completed_at', 'completedAt']),
    completion_notes: readString(value, ['completion_notes', 'completionNotes']),
    points: readNumber(value, ['points']) ?? 0,
  };
}

function parseApprovalJob(value: unknown): ApprovalJob | null {
  if (!isRecord(value)) return null;

  const blocks = readArray(value, ['blocks'])
    .map(parseBlock)
    .filter((b): b is ApprovalJobBlock => b !== null);

  return {
    id: readString(value, ['id']) ?? '',
    site_id: readString(value, ['site_id', 'siteId']) ?? '',
    site_name: readString(value, ['site_name', 'siteName']) ?? 'Unknown',
    scheduled_for: readString(value, ['scheduled_for', 'scheduledFor']) ?? '',
    assigned_user_id: readString(value, ['assigned_user_id', 'assignedUserId']) ?? '',
    assigned_user_name: readString(value, ['assigned_user_name', 'assignedUserName']) ?? 'Okänd',
    status: (readString(value, ['status']) as ApprovalJob['status'] | null) ?? 'completed',
    total_points: readNumber(value, ['total_points', 'totalPoints']) ?? 0,
    completed_at: readString(value, ['completed_at', 'completedAt']),
    approved_at: readString(value, ['approved_at', 'approvedAt']),
    approved_by: readString(value, ['approved_by', 'approvedBy']),
    completion_notes: readString(value, ['completion_notes', 'completionNotes']),
    blocks,
  };
}

export async function loadPendingApprovalJobs(sessionToken: string | null): Promise<ApprovalJob[]> {
  const response = await requestJson<ApprovalApiEnvelope>(
    '/api/ops/jobs/pending-approval',
    sessionToken,
    { method: 'GET' },
  );

  return readArray(response, ['jobs'])
    .map(parseApprovalJob)
    .filter((job): job is ApprovalJob => job !== null);
}

export async function approveJob(jobId: string, sessionToken: string | null): Promise<void> {
  await requestJson<ApprovalApiEnvelope>(
    '/api/ops/jobs/approve',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify({ jobId }),
    },
  );
}